/**
 * Markdown, defined once.
 *
 * **Markdown is the source, rendered exactly as written.** What is stored is
 * what was typed, and what is drawn is drawn from that: no editor that hides
 * the marks, and no passage through HTML and back. A body is handed to the
 * parser unchanged — a single newline is a soft break that reflows, because
 * bodies are written hard-wrapped and a break per source line splits every
 * wrapped sentence, separates the rows a table needs contiguous, and gaps
 * every line of a code block.
 *
 * What is not the parse is data: the extensions, the addresses a link may
 * hold, and the tools.
 */

import type { Format, FormatTool } from './format'
import { markdownOutline } from './markdown-outline'
import { markdownSearchText } from './markdown-text'

/**
 * The only schemes a link may point at. An address of any other kind — a
 * script, a data URI, a path into the app itself — is drawn as the link's
 * text and is not a link.
 *
 * A body may be written by a model and read by somebody else, so an address in
 * one is a request somebody else's browser would make. A path into the app is
 * the sharpest case: followed by a signed-in reader, it is a request in their
 * name.
 *
 * What this stops is a RELATIVE address, which the browser resolves against
 * the page the reader is on. An absolute address naming one of the app's own
 * hosts is a link like any other: it says where it goes, it opens in a tab of
 * its own, and telling it from somebody else's host would need the list of
 * hosts, which a format does not hold.
 */
export const MARKDOWN_LINK_SCHEMES: readonly string[] = [
  'http',
  'https',
  'mailto',
]

/**
 * The schemes whose address names a host, and so must carry one.
 *
 * A browser reads `https:/api` and `https:api` against the page it is on, as a
 * path of the same origin: the scheme alone is no proof the address leaves the
 * app. An address of these is a link only where two slashes and a host follow
 * the colon.
 */
const NAMES_A_HOST = new Set(['http', 'https'])

/** Whether an address is one a link may hold. */
export function isLinkAddress(address: string | null | undefined): boolean {
  /* A browser drops tabs and line breaks anywhere in an address before it
     reads one, so they are dropped here before this reads it. */
  const read = (address ?? '').replace(/[\t\n\r]/g, '').trim()
  const [, scheme = '', rest = ''] =
    /^([a-z][a-z0-9+.-]*):(.*)$/is.exec(read) ?? []
  const named = scheme.toLowerCase()
  if (!MARKDOWN_LINK_SCHEMES.includes(named)) return false
  return !NAMES_A_HOST.has(named) || /^\/\/[^/\\?#\s]/.test(rest)
}

/** What a markdown editor offers, in the order it offers them. */
const MARKDOWN_TOOLS: readonly FormatTool[] = [
  {
    id: 'bold',
    title: 'Bold',
    mark: 'B',
    command: { kind: 'wrap', before: '**', after: '**' },
  },
  {
    id: 'italic',
    title: 'Italic',
    mark: 'I',
    command: { kind: 'wrap', before: '_', after: '_' },
  },
  {
    id: 'link',
    title: 'Link',
    mark: 'Link',
    command: { kind: 'wrap', before: '[', after: '](url)' },
  },
  {
    id: 'code',
    title: 'Inline code',
    mark: '“ ”',
    command: { kind: 'wrap', before: '`', after: '`' },
  },
  {
    id: 'list',
    title: 'Bulleted list',
    mark: '•',
    command: { kind: 'prefix', prefix: '- ' },
  },
  {
    id: 'heading',
    title: 'Heading',
    mark: 'H',
    command: { kind: 'prefix', prefix: '## ' },
  },
]

export const markdown = {
  bodyStorage: 'inline',
  validate: () => true,
  outline: markdownOutline,
  searchText: markdownSearchText,
  tools: MARKDOWN_TOOLS,
} as const satisfies Format
